// MapLibre custom layer drawing a route at its computed altitude in 3D: a line at glide altitude
// plus a translucent curtain down to the ground, red where clearance is below the margin.

import maplibregl, { type CustomLayerInterface, type CustomRenderMethodInput, type Map } from 'maplibre-gl';

export interface Route3DPoint {
  lng: number;
  lat: number;
  alt: number;
  ground: number;
  low: boolean;
}

const VS = `
attribute vec3 a_pos;
attribute vec4 a_color;
uniform mat4 u_matrix;
varying vec4 v_color;
void main() {
  gl_Position = u_matrix * vec4(a_pos, 1.0);
  v_color = a_color;
}`;
const FS = `
precision mediump float;
varying vec4 v_color;
void main() { gl_FragColor = v_color; }`;

export class Route3DLayer implements CustomLayerInterface {
  readonly id = 'route-3d';
  readonly type = 'custom' as const;
  readonly renderingMode = '3d' as const;
  private map?: Map;
  private gl?: WebGLRenderingContext | WebGL2RenderingContext;
  private program?: WebGLProgram;
  private buf?: WebGLBuffer;
  private curtainCount = 0;
  private lineCount = 0;
  private points: Route3DPoint[] = [];
  private exaggeration = 1;

  setPoints(points: Route3DPoint[], exaggeration = 1) {
    this.points = points;
    this.exaggeration = exaggeration;
    if (this.gl) this.upload(this.gl);
    this.map?.triggerRepaint();
  }

  onAdd(map: Map, gl: WebGLRenderingContext | WebGL2RenderingContext) {
    this.map = map;
    this.gl = gl;
    const sh = (type: number, src: string) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, src);
      gl.compileShader(s);
      return s;
    };
    const p = gl.createProgram()!;
    gl.attachShader(p, sh(gl.VERTEX_SHADER, VS));
    gl.attachShader(p, sh(gl.FRAGMENT_SHADER, FS));
    gl.linkProgram(p);
    this.program = p;
    this.buf = gl.createBuffer()!;
    this.upload(gl);
  }

  private upload(gl: WebGLRenderingContext | WebGL2RenderingContext) {
    if (!this.buf) return;
    const v: number[] = [];
    const push = (pt: Route3DPoint, alt: number, c: [number, number, number, number]) => {
      const m = maplibregl.MercatorCoordinate.fromLngLat([pt.lng, pt.lat], alt * this.exaggeration);
      v.push(m.x, m.y, m.z, ...c);
    };
    const curtain = (low: boolean): [number, number, number, number] => (low ? [0.9, 0.1, 0.1, 0.35] : [0.1, 0.4, 0.95, 0.25]);
    const line = (low: boolean): [number, number, number, number] => (low ? [1, 0.1, 0.1, 1] : [0.05, 0.25, 0.9, 1]);
    const pts = this.points.filter((p) => Number.isFinite(p.alt));
    // curtain: two triangles per segment
    for (let i = 0; i + 1 < pts.length; i++) {
      const a = pts[i];
      const b = pts[i + 1];
      const c = curtain(a.low || b.low);
      push(a, a.ground, c);
      push(a, a.alt, c);
      push(b, b.ground, c);
      push(a, a.alt, c);
      push(b, b.alt, c);
      push(b, b.ground, c);
    }
    this.curtainCount = v.length / 7;
    for (let i = 0; i + 1 < pts.length; i++) {
      push(pts[i], pts[i].alt, line(pts[i].low));
      push(pts[i + 1], pts[i + 1].alt, line(pts[i + 1].low));
    }
    this.lineCount = v.length / 7 - this.curtainCount;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(v), gl.STATIC_DRAW);
  }

  onRemove() {
    if (this.gl && this.buf) this.gl.deleteBuffer(this.buf);
    if (this.gl && this.program) this.gl.deleteProgram(this.program);
    this.map = this.gl = this.buf = this.program = undefined;
  }

  render(gl: WebGLRenderingContext | WebGL2RenderingContext, o: CustomRenderMethodInput) {
    if (!this.program || !this.buf || this.lineCount === 0) return;
    gl.useProgram(this.program);
    // MapLibre v5: mainMatrix projects 0..1 Mercator coordinates (modelViewProjectionMatrix is
    // in world pixels).
    gl.uniformMatrix4fv(gl.getUniformLocation(this.program, 'u_matrix'), false, o.defaultProjectionData.mainMatrix as Float32Array);
    if ('bindVertexArray' in gl) gl.bindVertexArray(null); // don't clobber MapLibre's VAOs
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buf);
    const pos = gl.getAttribLocation(this.program, 'a_pos');
    const col = gl.getAttribLocation(this.program, 'a_color');
    gl.enableVertexAttribArray(pos);
    gl.enableVertexAttribArray(col);
    gl.vertexAttribPointer(pos, 3, gl.FLOAT, false, 28, 0);
    gl.vertexAttribPointer(col, 4, gl.FLOAT, false, 28, 12);
    gl.disable(gl.STENCIL_TEST);
    gl.disable(gl.CULL_FACE);
    gl.colorMask(true, true, true, true);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.enable(gl.DEPTH_TEST);
    gl.depthMask(false);
    gl.drawArrays(gl.TRIANGLES, 0, this.curtainCount);
    gl.lineWidth(3);
    gl.drawArrays(gl.LINES, this.curtainCount, this.lineCount);
    gl.depthMask(true);
  }
}
